import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Badge,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People,
  HealthAndSafety,
  School,
  Diversity3,
  Download,
  Add,
  CheckCircle,
  Warning,
  Pending,
  ThumbUp,
  Delete,
  CloudUpload,
  TrendingUp,
  TrendingDown,
  Work,
  Gavel,
  Info,
  Refresh,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useDropzone } from 'react-dropzone';
import { usePermissions } from '../../hooks/usePermissions';
import { ApprovalDialog } from '../shared/ApprovalDialog';
import { useApp } from '../../contexts/AuthContext';
import { socialService } from '../../services';

const COLORS = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6'];

// GRI-aligned data interfaces
interface EmploymentData {
  id: string;
  category: string;
  metric: string;
  value: number;
  unit: string;
  breakdown?: Record<string, number>;
  period: string;
  status: string;
  uploadedBy?: string;
}

interface HealthSafetyData {
  id: string;
  category: string;
  metric: string;
  value: number;
  unit: string;
  workerType: 'employee' | 'contractor';
  period: string;
  status: string;
  uploadedBy?: string;
}

interface DiversityData {
  id: string;
  category: string;
  metric: string;
  value: number;
  unit: string;
  governanceLevel?: string;
  period: string;
  status: string;
  uploadedBy?: string;
}

interface TrainingData {
  id: string;
  category: string;
  employeeCategory: string;
  hours: number;
  participants: number;
  period: string;
  status: string;
  uploadedBy?: string;
}

interface HumanRightsData {
  id: string;
  category: string;
  metric: string;
  value: number | string;
  period: string;
  status: string;
  uploadedBy?: string;
}


export const SocialDataCapture: React.FC = () => {
  const { user } = useApp();
  const permissions = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [employmentData, setEmploymentData] = useState<EmploymentData[]>([]);
  const [healthSafetyData, setHealthSafetyData] = useState<HealthSafetyData[]>([]);
  const [diversityData, setDiversityData] = useState<DiversityData[]>([]);
  const [trainingData, setTrainingData] = useState<TrainingData[]>([]);
  const [humanRightsData, setHumanRightsData] = useState<HumanRightsData[]>([]);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [empResponse, hsResponse, divResponse, trainResponse, hrResponse] = await Promise.all([
        socialService.employment.list(),
        socialService.healthSafety.list(),
        socialService.diversity.list(),
        socialService.training.list(),
        socialService.humanRights.list(),
      ]);

      // Transform employment data
      setEmploymentData(empResponse.entries.map((e: any) => ({
        id: e.id,
        category: e.gri_indicator,
        metric: e.metric_name,
        value: e.value,
        unit: e.unit,
        breakdown: e.breakdown,
        period: e.reporting_period,
        status: e.status,
        uploadedBy: e.created_by,
      })));

      // Transform health & safety data
      setHealthSafetyData(hsResponse.entries.map((e: any) => ({
        id: e.id,
        category: e.gri_indicator,
        metric: e.metric_name,
        value: e.value,
        unit: e.unit,
        workerType: e.worker_type || 'employee',
        period: e.reporting_period,
        status: e.status,
        uploadedBy: e.created_by,
      })));

      // Transform diversity data
      setDiversityData(divResponse.entries.map((e: any) => ({
        id: e.id,
        category: e.gri_indicator,
        metric: e.metric_name,
        value: e.value,
        unit: e.unit,
        governanceLevel: e.governance_level,
        period: e.reporting_period,
        status: e.status,
        uploadedBy: e.created_by,
      })));

      // Transform training data
      setTrainingData(trainResponse.entries.map((e: any) => ({
        id: e.id,
        category: e.gri_indicator || 'GRI 404-1',
        employeeCategory: e.employee_category,
        hours: e.hours,
        participants: e.participants,
        period: e.reporting_period,
        status: e.status,
        uploadedBy: e.created_by,
      })));

      // Transform human rights data
      setHumanRightsData(hrResponse.entries.map((e: any) => ({
        id: e.id,
        category: e.gri_indicator,
        metric: e.metric_name,
        value: e.value,
        period: e.reporting_period,
        status: e.status,
        uploadedBy: e.created_by,
      })));
    } catch (err: any) {
      setError(err.message || 'Failed to load social data');
      // Keep empty arrays - no mock data fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
    onDrop: (files) => {
      console.log('Files uploaded:', files);
      setUploadDialogOpen(false);
    },
  });

  // Calculate summary metrics
  const totalEmployees = employmentData.find(d => d.metric === 'Total Employees')?.value || 0;
  const turnoverRate = employmentData.find(d => d.metric === 'Employee Turnover')?.value || 0;
  const ltifr = healthSafetyData.find(d => d.metric === 'LTIFR' && d.workerType === 'employee')?.value || 0;
  const femalePercentage = diversityData.find(d => d.metric === 'Female Employees')?.value || 0;
  const allEmployeesTraining = trainingData.find(d => d.employeeCategory === 'All Employees');
  const avgTrainingHours = allEmployeesTraining && allEmployeesTraining.participants > 0
    ? (allEmployeesTraining.hours / allEmployeesTraining.participants).toFixed(1)
    : 0;

  const getStatusChip = (status: string) => {
    const config: Record<string, { color: string; bg: string; label: string; icon: React.ReactElement }> = {
      approved: { color: '#059669', bg: '#d1fae5', label: 'Approved', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
      submitted: { color: '#2563eb', bg: '#dbeafe', label: 'Pending', icon: <Pending sx={{ fontSize: 14 }} /> },
      draft: { color: '#d97706', bg: '#fef3c7', label: 'Draft', icon: <Pending sx={{ fontSize: 14 }} /> },
      flagged: { color: '#dc2626', bg: '#fee2e2', label: 'Flagged', icon: <Warning sx={{ fontSize: 14 }} /> },
    };
    const c = config[status] || { color: '#6b7280', bg: '#f3f4f6', label: status, icon: <Pending sx={{ fontSize: 14 }} /> };

    return (
      <Chip
        icon={c.icon}
        label={c.label}
        size="small"
        sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: '0.75rem', height: 24, '& .MuiChip-icon': { color: c.color } }}
      />
    );
  };

  // Charts data - derived from API data
  const genderDistribution = (() => {
    const femalePercent = diversityData.find(d => d.metric === 'Female Employees')?.value || 0;
    const malePercent = 100 - femalePercent;
    return [
      { name: 'Male', value: malePercent },
      { name: 'Female', value: femalePercent },
    ];
  })();

  const ageDistribution = (() => {
    const under30 = diversityData.find(d => d.metric === 'Employees Under 30')?.value || 0;
    const age30to50 = diversityData.find(d => d.metric === 'Employees 30-50')?.value || 0;
    const over50 = diversityData.find(d => d.metric === 'Employees Over 50')?.value || 0;
    return [
      { name: 'Under 30', value: under30 },
      { name: '30-50', value: age30to50 },
      { name: 'Over 50', value: over50 },
    ];
  })();

  const trainingByCategory = (() => {
    const mgmtData = trainingData.find(d => d.employeeCategory === 'Management');
    const nonMgmtData = trainingData.find(d => d.employeeCategory === 'Non-Management');
    return [
      { category: 'Management', hours: mgmtData && mgmtData.participants > 0 ? mgmtData.hours / mgmtData.participants : 0 },
      { category: 'Non-Management', hours: nonMgmtData && nonMgmtData.participants > 0 ? nonMgmtData.hours / nonMgmtData.participants : 0 },
    ].filter(d => d.hours > 0);
  })();

  const tabLabels = [
    { label: 'Employment', icon: <Work />, gri: 'GRI 401' },
    { label: 'Health & Safety', icon: <HealthAndSafety />, gri: 'GRI 403' },
    { label: 'Diversity', icon: <Diversity3 />, gri: 'GRI 405-406' },
    { label: 'Training', icon: <School />, gri: 'GRI 404' },
    { label: 'Human Rights', icon: <Gavel />, gri: 'GRI 407-412' },
  ];

  const pendingCount = [...employmentData, ...healthSafetyData, ...diversityData, ...trainingData, ...humanRightsData]
    .filter(d => d.status === 'submitted').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827', mb: 0.25, fontSize: '1.1rem' }}>
            Social & People Metrics
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            Workforce data collection aligned with GRI Social Standards (GRI 400 series)
          </Typography>
        </Box>
        <IconButton onClick={fetchData} size="small" sx={{ color: '#6b7280' }}>
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 2 }}>
            <Box sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <People sx={{ color: '#2563eb', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
                    Total Workforce
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>
                    {totalEmployees.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>employees</Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 2 }}>
            <Box sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <TrendingDown sx={{ color: '#059669', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
                    Turnover Rate
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>
                    {turnoverRate}%
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingDown sx={{ fontSize: 12, color: '#059669' }} />
                <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.65rem' }}>-1.2% YoY</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 2 }}>
            <Box sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <HealthAndSafety sx={{ color: '#059669', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
                    LTIFR
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#059669', fontSize: '1.1rem' }}>
                    {ltifr}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>per 200k hrs</Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 2 }}>
            <Box sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: '#fce7f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Diversity3 sx={{ color: '#db2777', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
                    Female Workforce
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>
                    {femalePercentage}%
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUp sx={{ fontSize: 12, color: '#059669' }} />
                <Typography variant="caption" sx={{ color: '#059669', fontSize: '0.65rem' }}>+2.1% YoY</Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 2 }}>
            <Box sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, backgroundColor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <School sx={{ color: '#d97706', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}>
                    Avg Training
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>
                    {avgTrainingHours}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>hours/employee</Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs and Actions */}
      <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 2 }}>
        <Box sx={{ px: 2, pt: 1.5, borderBottom: '1px solid #e5e7eb' }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                color: '#6b7280',
                minHeight: 40,
                fontSize: '0.8rem',
                px: 2,
              },
              '& .Mui-selected': {
                color: '#111827',
                fontWeight: 600,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#3b82f6',
                height: 2,
                borderRadius: '2px 2px 0 0',
              },
            }}
          >
            {tabLabels.map((tab, index) => (
              <Tab
                key={index}
                label={tab.label}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flexGrow: 1 }} />

          {permissions.canExport && (
            <Button variant="outlined" size="small" startIcon={<Download />}
              sx={{ borderColor: '#d1d5db', color: '#374151', borderRadius: 2, textTransform: 'none', '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' } }}>
              Download Template
            </Button>
          )}

          {permissions.canCreate && (
            <>
              <Button variant="outlined" size="small" startIcon={<CloudUpload />} onClick={() => setUploadDialogOpen(true)}
                sx={{ borderColor: '#d1d5db', color: '#374151', borderRadius: 2, textTransform: 'none', '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' } }}>
                Upload Data
              </Button>
              <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setEntryDialogOpen(true)}
                sx={{ backgroundColor: '#3b82f6', borderRadius: 2, textTransform: 'none', px: 2.5, '&:hover': { backgroundColor: '#2563eb' } }}>
                Add Entry
              </Button>
            </>
          )}

          {pendingCount > 0 && permissions.canApprove && (
            <Badge badgeContent={pendingCount} color="warning">
              <Chip label="Pending Approvals" size="small" sx={{ backgroundColor: '#fef3c7', color: '#92400e' }} />
            </Badge>
          )}
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.5, backgroundColor: '#f0f9ff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info sx={{ fontSize: 18, color: '#0284c7' }} />
          <Typography variant="caption" sx={{ color: '#0369a1' }}>
            {activeTab === 0 && 'Employment data following GRI 401: Employment and GRI 2-7/2-8: Employees and Workers'}
            {activeTab === 1 && 'Occupational Health & Safety data following GRI 403: Occupational Health and Safety'}
            {activeTab === 2 && 'Diversity metrics following GRI 405: Diversity and Equal Opportunity, GRI 406: Non-discrimination'}
            {activeTab === 3 && 'Training data following GRI 404: Training and Education'}
            {activeTab === 4 && 'Human Rights data following GRI 407-412: Freedom of Association, Child Labor, Forced Labor, Human Rights Assessment'}
          </Typography>
        </Box>
      </Card>

      {/* Employment Tab */}
      {activeTab === 0 && (
        <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>GRI Indicator</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Metric</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="right">Value</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Breakdown</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employmentData.map((row) => {
                  const actions = permissions.getEntryActions(row.status, row.uploadedBy);
                  return (
                    <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                      <TableCell><Chip label={row.category} size="small" sx={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 500, fontSize: '0.7rem' }} /></TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{row.metric}</Typography>
                        {row.uploadedBy && <Typography variant="caption" sx={{ color: '#9ca3af' }}>by {row.uploadedBy}</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{row.value.toLocaleString()}</Typography>
                        <Typography variant="caption" sx={{ color: '#9ca3af' }}>{row.unit}</Typography>
                      </TableCell>
                      <TableCell>
                        {row.breakdown ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {Object.entries(row.breakdown).map(([key, val]) => (
                              <Chip key={key} label={`${key}: ${val}`} size="small" sx={{ backgroundColor: '#f3f4f6', fontSize: '0.65rem', height: 20 }} />
                            ))}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{getStatusChip(row.status)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {actions.canApprove && (
                            <Tooltip title="Approve">
                              <IconButton size="small" onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }} sx={{ color: '#059669', '&:hover': { backgroundColor: '#d1fae5' } }}>
                                <ThumbUp sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {actions.canDelete && (
                            <Tooltip title="Delete">
                              <IconButton size="small" sx={{ color: '#6b7280', '&:hover': { backgroundColor: '#f3f4f6' } }}>
                                <Delete sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Health & Safety Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            {(() => {
              const fatalities = healthSafetyData.find(d => d.metric?.includes('Fatalities') && d.workerType === 'employee')?.value || 0;
              return (
                <Card sx={{ p: 2.5, border: fatalities === 0 ? '2px solid #d1fae5' : '2px solid #fee2e2', boxShadow: 'none', borderRadius: 3, backgroundColor: fatalities === 0 ? '#f0fdf4' : '#fef2f2' }}>
                  <Typography variant="caption" sx={{ color: fatalities === 0 ? '#059669' : '#dc2626', fontWeight: 600, textTransform: 'uppercase' }}>{fatalities === 0 ? 'Zero Fatalities' : 'Fatalities'}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: fatalities === 0 ? '#059669' : '#dc2626', my: 1 }}>{fatalities}</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Work-related fatalities</Typography>
                </Card>
              );
            })()}
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase' }}>LTIFR</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', my: 1 }}>{ltifr || '-'}</Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>Lost Time Injury Frequency Rate</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            {(() => {
              const trir = healthSafetyData.find(d => d.metric === 'TRIR' && d.workerType === 'employee')?.value || 0;
              return (
                <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase' }}>TRIR</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', my: 1 }}>{trir || '-'}</Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Total Recordable Incident Rate</Typography>
                </Card>
              );
            })()}
          </Grid>
          <Grid item xs={12} md={3}>
            {(() => {
              const trainingHours = healthSafetyData.find(d => d.metric === 'Safety Training Hours')?.value || 0;
              return (
                <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase' }}>Safety Training</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', my: 1 }}>{trainingHours ? trainingHours.toLocaleString() : '-'}</Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Hours</Typography>
                </Card>
              );
            })()}
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>GRI 403-9: Work-Related Injuries</Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>GRI Indicator</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Metric</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Worker Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="right">Value</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {healthSafetyData.map((row) => {
                      const actions = permissions.getEntryActions(row.status, row.uploadedBy);
                      return (
                        <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                          <TableCell><Chip label={row.category} size="small" sx={{ backgroundColor: '#d1fae5', color: '#065f46', fontWeight: 500, fontSize: '0.7rem' }} /></TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{row.metric}</Typography></TableCell>
                          <TableCell>
                            <Chip label={row.workerType === 'employee' ? 'Employee' : 'Contractor'} size="small"
                              sx={{ backgroundColor: row.workerType === 'employee' ? '#dbeafe' : '#fef3c7', color: row.workerType === 'employee' ? '#1e40af' : '#92400e', fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: row.value === 0 && row.metric.includes('Fatalities') ? '#059669' : '#111827' }}>
                              {row.value.toLocaleString()}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9ca3af' }}>{row.unit}</Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(row.status)}</TableCell>
                          <TableCell align="center">
                            {actions.canApprove && (
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }} sx={{ color: '#059669', '&:hover': { backgroundColor: '#d1fae5' } }}>
                                  <ThumbUp sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Diversity Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: 350 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Gender Distribution (GRI 405-1)</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={genderDistribution} cx="50%" cy="50%" labelLine={false} label={({ name, value }) => `${name}: ${value}%`} outerRadius={100} fill="#8884d8" dataKey="value">
                    {genderDistribution.map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: 350 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Age Distribution (GRI 405-1)</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={ageDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="value" fill="#3b82f6" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>GRI Indicator</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Metric</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Level</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="right">Value</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {diversityData.map((row) => {
                      const actions = permissions.getEntryActions(row.status, row.uploadedBy);
                      return (
                        <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                          <TableCell><Chip label={row.category} size="small" sx={{ backgroundColor: '#fce7f3', color: '#9d174d', fontWeight: 500, fontSize: '0.7rem' }} /></TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{row.metric}</Typography></TableCell>
                          <TableCell>{row.governanceLevel ? <Chip label={row.governanceLevel} size="small" sx={{ backgroundColor: '#f3f4f6', fontSize: '0.7rem' }} /> : '-'}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>{row.value}</Typography>
                            <Typography variant="caption" sx={{ color: '#9ca3af' }}>{row.unit}</Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(row.status)}</TableCell>
                          <TableCell align="center">
                            {actions.canApprove && (
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }} sx={{ color: '#059669', '&:hover': { backgroundColor: '#d1fae5' } }}>
                                  <ThumbUp sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Training Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {(() => {
              const allEmployeesData = trainingData.find(d => d.employeeCategory === 'All Employees');
              const totalHours = allEmployeesData?.hours || 0;
              return (
                <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase' }}>Total Training Hours</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', my: 1 }}>{totalHours ? totalHours.toLocaleString() : '-'}</Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>All employees</Typography>
                </Card>
              );
            })()}
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase' }}>Avg Hours per Employee</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', my: 1 }}>{avgTrainingHours || '-'}</Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af' }}>hours/employee</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            {(() => {
              const perfReviewData = trainingData.find(d => d.employeeCategory === 'Performance Reviews');
              const allEmployeesData = trainingData.find(d => d.employeeCategory === 'All Employees');
              const reviewPercent = perfReviewData && allEmployeesData && allEmployeesData.participants > 0
                ? ((perfReviewData.participants / allEmployeesData.participants) * 100).toFixed(1)
                : null;
              return (
                <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase' }}>Performance Reviews</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', my: 1 }}>{reviewPercent ? `${reviewPercent}%` : '-'}</Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Employees reviewed (GRI 404-3)</Typography>
                </Card>
              );
            })()}
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, height: 350 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Training Hours by Category</Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={trainingByCategory} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" />
                  <YAxis dataKey="category" type="category" width={120} />
                  <RechartsTooltip />
                  <Bar dataKey="hours" fill="#3b82f6" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>GRI 404-1: Average Training Hours</Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Employee Category</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }} align="right">Total Hours</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }} align="right">Participants</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }} align="right">Avg/Person</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trainingData.filter(d => d.category === 'GRI 404-1').map((row) => (
                      <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{row.employeeCategory}</Typography></TableCell>
                        <TableCell align="right">{row.hours.toLocaleString()}</TableCell>
                        <TableCell align="right">{row.participants.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{(row.hours / row.participants).toFixed(1)}</TableCell>
                        <TableCell>{getStatusChip(row.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Human Rights Tab */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            {(() => {
              const childLaborRisk = humanRightsData.find(d => d.metric?.includes('Child Labor'))?.value as number || 0;
              const isZero = childLaborRisk === 0;
              return (
                <Card sx={{ p: 2.5, border: isZero ? '2px solid #d1fae5' : '2px solid #fee2e2', boxShadow: 'none', borderRadius: 3, backgroundColor: isZero ? '#f0fdf4' : '#fef2f2' }}>
                  <Typography variant="caption" sx={{ color: isZero ? '#059669' : '#dc2626', fontWeight: 600, textTransform: 'uppercase' }}>Child Labor Risk</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: isZero ? '#059669' : '#dc2626', my: 1 }}>{childLaborRisk}</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Operations identified with risk (GRI 408-1)</Typography>
                </Card>
              );
            })()}
          </Grid>
          <Grid item xs={12} md={4}>
            {(() => {
              const forcedLaborRisk = humanRightsData.find(d => d.metric?.includes('Forced Labor'))?.value as number || 0;
              const isZero = forcedLaborRisk === 0;
              return (
                <Card sx={{ p: 2.5, border: isZero ? '2px solid #d1fae5' : '2px solid #fee2e2', boxShadow: 'none', borderRadius: 3, backgroundColor: isZero ? '#f0fdf4' : '#fef2f2' }}>
                  <Typography variant="caption" sx={{ color: isZero ? '#059669' : '#dc2626', fontWeight: 600, textTransform: 'uppercase' }}>Forced Labor Risk</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: isZero ? '#059669' : '#dc2626', my: 1 }}>{forcedLaborRisk}</Typography>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>Operations identified with risk (GRI 409-1)</Typography>
                </Card>
              );
            })()}
          </Grid>
          <Grid item xs={12} md={4}>
            {(() => {
              const hrTraining = humanRightsData.find(d => d.metric?.includes('Trained on Human Rights'))?.value;
              return (
                <Card sx={{ p: 2.5, border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500, textTransform: 'uppercase' }}>HR Training</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', my: 1 }}>{hrTraining !== undefined ? `${hrTraining}%` : '-'}</Typography>
                  <Typography variant="caption" sx={{ color: '#9ca3af' }}>Employees trained on human rights (GRI 412-2)</Typography>
                </Card>
              );
            })()}
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e5e7eb', boxShadow: 'none', borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Human Rights Assessment (GRI 407-412)</Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>GRI Indicator</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Metric</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="right">Value</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#374151', py: 1.5 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {humanRightsData.map((row) => {
                      const actions = permissions.getEntryActions(row.status, row.uploadedBy);
                      const isZeroRisk = row.value === 0 && (row.metric.includes('Risk') || row.metric.includes('Child') || row.metric.includes('Forced'));
                      return (
                        <TableRow key={row.id} sx={{ '&:hover': { backgroundColor: '#f9fafb' } }}>
                          <TableCell><Chip label={row.category} size="small" sx={{ backgroundColor: '#ede9fe', color: '#5b21b6', fontWeight: 500, fontSize: '0.7rem' }} /></TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{row.metric}</Typography></TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 600, color: isZeroRisk ? '#059669' : '#111827' }}>
                              {typeof row.value === 'number' ? row.value.toLocaleString() : row.value}
                              {row.metric.includes('Trained') || row.metric.includes('Reviews') ? '%' : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(row.status)}</TableCell>
                          <TableCell align="center">
                            {actions.canApprove && (
                              <Tooltip title="Approve">
                                <IconButton size="small" onClick={() => { setSelectedEntry(row); setApprovalDialogOpen(true); }} sx={{ color: '#059669', '&:hover': { backgroundColor: '#d1fae5' } }}>
                                  <ThumbUp sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Upload Social Data</Typography>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>{tabLabels[activeTab].label} - {tabLabels[activeTab].gri}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box {...getRootProps()} sx={{ border: '2px dashed', borderColor: isDragActive ? '#3b82f6' : '#d1d5db', borderRadius: 3, p: 5, textAlign: 'center', backgroundColor: isDragActive ? '#eff6ff' : '#fafafa', cursor: 'pointer', transition: 'all 0.2s ease', '&:hover': { borderColor: '#3b82f6', backgroundColor: '#eff6ff' } }}>
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 56, color: isDragActive ? '#3b82f6' : '#9ca3af', mb: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827', mb: 0.5 }}>{isDragActive ? 'Drop your file here' : 'Drag & drop your file here'}</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>or click to browse</Typography>
            <Chip label="Accepts Excel (.xlsx) or CSV files" size="small" sx={{ backgroundColor: '#f3f4f6', color: '#6b7280' }} />
          </Box>
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f9fafb', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', display: 'block', mb: 1 }}>Need a template?</Typography>
            <Button size="small" startIcon={<Download />} sx={{ textTransform: 'none', color: '#3b82f6' }}>Download {tabLabels[activeTab].label} template</Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setUploadDialogOpen(false)} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={entryDialogOpen} onClose={() => setEntryDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ backgroundColor: '#3b82f6', color: '#fff', py: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Add {tabLabels[activeTab].label} Data</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>{tabLabels[activeTab].gri}</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>GRI Indicator</InputLabel>
              <Select label="GRI Indicator" defaultValue="">
                {activeTab === 0 && <>
                  <MenuItem value="GRI 401-1">GRI 401-1: New Hires and Turnover</MenuItem>
                  <MenuItem value="GRI 401-2">GRI 401-2: Benefits</MenuItem>
                  <MenuItem value="GRI 401-3">GRI 401-3: Parental Leave</MenuItem>
                  <MenuItem value="GRI 2-7">GRI 2-7: Employees</MenuItem>
                  <MenuItem value="GRI 2-8">GRI 2-8: Workers</MenuItem>
                </>}
                {activeTab === 1 && <>
                  <MenuItem value="GRI 403-9">GRI 403-9: Work-Related Injuries</MenuItem>
                  <MenuItem value="GRI 403-10">GRI 403-10: Work-Related Ill Health</MenuItem>
                  <MenuItem value="GRI 403-5">GRI 403-5: Training</MenuItem>
                </>}
                {activeTab === 2 && <>
                  <MenuItem value="GRI 405-1">GRI 405-1: Diversity</MenuItem>
                  <MenuItem value="GRI 405-2">GRI 405-2: Pay Ratio</MenuItem>
                  <MenuItem value="GRI 406-1">GRI 406-1: Discrimination</MenuItem>
                </>}
                {activeTab === 3 && <>
                  <MenuItem value="GRI 404-1">GRI 404-1: Training Hours</MenuItem>
                  <MenuItem value="GRI 404-3">GRI 404-3: Performance Reviews</MenuItem>
                </>}
                {activeTab === 4 && <>
                  <MenuItem value="GRI 407-1">GRI 407-1: Freedom of Association</MenuItem>
                  <MenuItem value="GRI 408-1">GRI 408-1: Child Labor</MenuItem>
                  <MenuItem value="GRI 409-1">GRI 409-1: Forced Labor</MenuItem>
                  <MenuItem value="GRI 412-1">GRI 412-1: Human Rights Reviews</MenuItem>
                  <MenuItem value="GRI 412-2">GRI 412-2: Human Rights Training</MenuItem>
                </>}
              </Select>
            </FormControl>
            <TextField label="Metric Name" fullWidth />
            <TextField label="Value" type="number" fullWidth />
            <TextField label="Unit" fullWidth placeholder="e.g., %, employees, hours" />
            <FormControl fullWidth>
              <InputLabel>Reporting Period</InputLabel>
              <Select label="Reporting Period" defaultValue="2024">
                <MenuItem value="2024">2024</MenuItem>
                <MenuItem value="2024-Q1">2024 Q1</MenuItem>
                <MenuItem value="2024-Q2">2024 Q2</MenuItem>
                <MenuItem value="2024-Q3">2024 Q3</MenuItem>
                <MenuItem value="2024-Q4">2024 Q4</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Notes (optional)" multiline rows={2} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEntryDialogOpen(false)} sx={{ color: '#6b7280', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" sx={{ backgroundColor: '#059669', textTransform: 'none', '&:hover': { backgroundColor: '#047857' } }}>Submit for Approval</Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      {selectedEntry && (
        <ApprovalDialog
          open={approvalDialogOpen}
          onClose={() => { setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onApprove={() => { setApprovalDialogOpen(false); setSelectedEntry(null); }}
          onReject={() => { setApprovalDialogOpen(false); setSelectedEntry(null); }}
          entryData={{
            category: selectedEntry.category || selectedEntry.metric,
            value: selectedEntry.value,
            unit: selectedEntry.unit || '',
            uploadedBy: selectedEntry.uploadedBy,
            uploadedAt: selectedEntry.uploadedAt,
          }}
        />
      )}
    </Box>
  );
};
